import Papa from "papaparse";
import {Constants} from "../../../constants/constants";
import {JobStopType} from "../../../utils/stops/StopUtils";
import {CsvImportClient, getCsvImportStatus, submitCsvImportOrders} from "./CsvImport.api";
import {
  CsvImportCreatedOrder,
  CsvImportCreateProgress,
  CsvImportFailedOrder,
  CsvImportKnownValues,
  CsvImportOrder,
  CsvImportResult,
  CsvImportStatus,
  CsvImportStop
} from "./types";

type CsvColumnKey =
  | "orderNumber"
  | "customerCode"
  | "stopType"
  | "address"
  | "city"
  | "state"
  | "zip"
  | "earlyDateTime"
  | "lateDateTime"
  | "scheduledDateTime"
  | "service"
  | "orderReference"
  | "pieces"
  | "weight"
  | "vehicle"
  | "caller"
  | "name"
  | "phone"
  | "room"
  | "note";

type CsvColumn = {key: CsvColumnKey; label: string; required: boolean; aliases: string[]};

// Aliases are compared after normalizeHeader (lowercase, letters and digits only)
export const CSV_IMPORT_COLUMNS: CsvColumn[] = [
  {key: "orderNumber", label: "OrderNumber", required: true, aliases: ["ordernumber", "order", "orderno", "jobnumber"]},
  {key: "customerCode", label: "CustomerCode", required: true, aliases: ["customercode", "customer", "custcode"]},
  {key: "service", label: "Service", required: true, aliases: ["service", "servicetype"]},
  {key: "stopType", label: "StopType", required: true, aliases: ["stoptype", "type"]},
  {key: "address", label: "Address", required: true, aliases: ["address", "address1", "street", "streetaddress"]},
  {key: "city", label: "City", required: true, aliases: ["city"]},
  {key: "state", label: "State", required: true, aliases: ["state", "st", "province"]},
  {key: "zip", label: "Zip", required: true, aliases: ["zip", "zipcode", "postalcode", "postal"]},
  {
    key: "scheduledDateTime",
    label: "ScheduledDateTime",
    required: true,
    aliases: ["scheduleddatetime", "scheduled", "scheduledtime"]
  },
  {
    key: "earlyDateTime",
    label: "EarlyDateTime",
    required: false,
    aliases: ["earlydatetime", "early", "earlytime", "ready", "readytime", "readydatetime"]
  },
  {
    key: "lateDateTime",
    label: "LateDateTime",
    required: false,
    aliases: ["latedatetime", "late", "latetime", "due", "duetime", "duedatetime"]
  },
  {
    key: "orderReference",
    label: "OrderReference",
    required: false,
    aliases: ["orderreference", "orderref", "reference", "ref"]
  },
  {key: "pieces", label: "Pieces", required: false, aliases: ["pieces", "piececount", "qty", "quantity"]},
  {key: "weight", label: "Weight", required: false, aliases: ["weight"]},
  // CMS vehicle type description, checked against the tenant's (CMS would apply the customer default instead)
  {key: "vehicle", label: "Vehicle", required: false, aliases: ["vehicle", "vehicletype", "truck"]},
  {key: "caller", label: "Caller", required: false, aliases: ["caller", "callername", "contact", "requestedby"]},
  {key: "name", label: "Name", required: false, aliases: ["name", "stopname", "company", "companyname"]},
  {key: "phone", label: "Phone", required: false, aliases: ["phone", "phonenumber"]},
  {key: "room", label: "Room", required: false, aliases: ["room", "suite"]},
  {key: "note", label: "Note", required: false, aliases: ["note", "notes", "instructions"]}
];

export const CSV_IMPORT_REQUIRED_LABELS = CSV_IMPORT_COLUMNS.filter((column) => column.required).map(
  (column) => column.label
);

const STOP_TYPES: Record<string, JobStopType> = {
  p: JobStopType.PICKUP,
  pickup: JobStopType.PICKUP,
  d: JobStopType.DELIVERY,
  delivery: JobStopType.DELIVERY,
  b: JobStopType.BOTH,
  both: JobStopType.BOTH
};

// Largest 32-bit Int, the GraphQL Int limit for the stop input
const MAX_PIECES = 2147483647;

// Weight is kept to 2 decimals (2.222 -> 2.22). Shifting through the exponent avoids binary
// float rounding such as 1.005 * 100 = 100.49999
const roundWeight = (value: string) => {
  const hundredths = Math.round(Number(`${value}e2`));
  return Number(`${hundredths}e-2`);
};

// Order-level columns: every row of an order must agree on the value. Pieces and Weight are per row (stop)
const ORDER_LEVEL_KEYS: CsvColumnKey[] = ["customerCode", "service", "orderReference", "vehicle", "caller"];

export const normalizeHeader = (header: string) => header.toLowerCase().replace(/[^a-z0-9]/g, "");

const columnLabel = (key: CsvColumnKey) => CSV_IMPORT_COLUMNS.find((column) => column.key === key)?.label ?? key;

// "CustomerCode" -> "Customer Code", as an issue names the column
const issueLabel = (key: CsvColumnKey) => columnLabel(key).replace(/([a-z])([A-Z])/g, "$1 $2");

// An issue lists up to this many values and orders, then "..."
const MAX_ISSUE_ITEMS = 10;

const listItems = (items: string[]) =>
  [...items.slice(0, MAX_ISSUE_ITEMS), ...(items.length > MAX_ISSUE_ITEMS ? ["..."] : [])].join(", ");

/**
 * Collects the issues of the rows and orders as one line per kind: the column, its distinct values, the
 * problem and the orders that have it, e.g. "Service 'X', 'Y' does not exist (ORD-1, ORD-2)".
 */
class CsvImportIssues {
  count = 0;
  private readonly kinds = new Map<
    string,
    {subject: string; problem: string; values: Set<string>; orders: Set<string>}
  >();

  add(order: string, subject: string, problem: string, value?: string) {
    const key = `${subject}|${problem}`;
    const kind = this.kinds.get(key) ?? {subject, problem, values: new Set<string>(), orders: new Set<string>()};
    if (value !== undefined) kind.values.add(value);
    kind.orders.add(order);
    this.kinds.set(key, kind);
    this.count++;
  }

  lines() {
    return [...this.kinds.values()].map(({subject, problem, values, orders}) => {
      const quoted = values.size > 0 ? listItems([...values].map((value) => `'${value}'`)) : "";
      return `${[subject, quoted, problem].filter(Boolean).join(" ")} (${listItems([...orders])})`;
    });
  }
}

// yyyy-M-d H:mm[:ss] or yyyy-M-dTH:mm[:ss] (zero padding optional, as Excel's yyyy-mm-dd h:mm writes it)
const ISO_DATE_TIME = /^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/;
// M/d/yyyy H:mm[:ss] or M/d/yyyy h:mm[:ss] AM/PM
const US_DATE_TIME = /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{2})(?::(\d{2}))?(?: ?([ap]m))?$/i;

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * Parses a date-time as written in the file and returns the same wall-clock time
 * ("yyyy-MM-dd'T'HH:mm:ss"). Built from the parsed fields, never from a local Date: the browser's
 * timezone (UTC conversion, DST gaps) must not change a stop-local time.
 */
export const parseDateTime = (value: string): string | null => {
  let year: number, month: number, day: number, hour: number, minute: number, second: number;
  const iso = ISO_DATE_TIME.exec(value);
  const us = iso ? null : US_DATE_TIME.exec(value);
  if (iso) {
    [year, month, day, hour, minute, second] = iso.slice(1, 7).map((part) => Number(part ?? 0));
  } else if (us) {
    [month, day, year, hour, minute, second] = us.slice(1, 7).map((part) => Number(part ?? 0));
    const meridiem = us[7]?.toLowerCase();
    if (meridiem) {
      if (hour < 1 || hour > 12) return null;
      hour = (hour % 12) + (meridiem === "pm" ? 12 : 0);
    }
  } else {
    return null;
  }

  // UTC only validates the calendar date (e.g. Feb 29 in a non-leap year); it is never converted
  const calendarDate = new Date(Date.UTC(year, month - 1, day));
  const isValidDate =
    calendarDate.getUTCFullYear() === year &&
    calendarDate.getUTCMonth() === month - 1 &&
    calendarDate.getUTCDate() === day;
  if (!isValidDate || year < 2000 || year > 2099 || hour > 23 || minute > 59 || second > 59) {
    return null;
  }
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}`;
};

const isBlankRow = (row: string[]) => row.every((cell) => !cell?.trim());

// stop is null when the row has issues; the row's order values are still checked with its order
type ParsedRow = {order: string; orderValues: Partial<Record<CsvColumnKey, string>>; stop: CsvImportStop | null};

type RowValue = (key: CsvColumnKey) => string;

const parseRowDateTimes = (value: RowValue, order: string, issues: CsvImportIssues) => {
  const dateTimes: Partial<Record<CsvColumnKey, string>> = {};
  for (const key of ["scheduledDateTime", "earlyDateTime", "lateDateTime"] as CsvColumnKey[]) {
    const text = value(key);
    if (!text) continue;
    const parsed = parseDateTime(text);
    if (parsed) {
      dateTimes[key] = parsed;
    } else {
      issues.add(order, issueLabel(key), "is not a valid date-time", text);
    }
  }
  return dateTimes;
};

const checkRowNumbers = (value: RowValue, order: string, issues: CsvImportIssues) => {
  if (value("pieces") && !/^\d+$/.test(value("pieces"))) {
    issues.add(order, "Pieces", "must be a whole number", value("pieces"));
  } else if (Number(value("pieces")) > MAX_PIECES) {
    issues.add(order, "Pieces", `must be ${MAX_PIECES.toLocaleString("en-US")} or less`, value("pieces"));
  }
  if (value("weight") && !/^\d+(\.\d+)?$/.test(value("weight"))) {
    issues.add(order, "Weight", "must be a number of 0 or more", value("weight"));
  }
};

const toOrderValues = (value: RowValue) => {
  const orderValues: Partial<Record<CsvColumnKey, string>> = {};
  for (const key of ORDER_LEVEL_KEYS) {
    if (value(key)) orderValues[key] = value(key);
  }
  return orderValues;
};

const parseRow = (
  row: string[],
  rowNumber: number,
  columnIndexes: Partial<Record<CsvColumnKey, number>>,
  lastTitledIndex: number,
  issues: CsvImportIssues
): ParsedRow => {
  const value = (key: CsvColumnKey) => {
    const index = columnIndexes[key];
    return index === undefined ? "" : (row[index] ?? "").trim();
  };
  const issueCount = issues.count;
  // Issues name the row's order, or the row when it has no OrderNumber
  const order = value("orderNumber") || `row ${rowNumber}`;

  // An unquoted comma pushes a value past the last titled column; blank cells there (Excel's
  // trailing commas) are fine, and untitled columns before it (e.g. a pandas index) stay ignored
  if (row.slice(lastTitledIndex + 1).some((cell) => cell?.trim())) {
    issues.add(order, "", "Rows have more values than the header, check for unquoted commas");
  }

  for (const column of CSV_IMPORT_COLUMNS) {
    if (column.required && !value(column.key)) {
      issues.add(order, issueLabel(column.key), "is required");
    }
  }

  const stopTypeText = value("stopType");
  const stopType = STOP_TYPES[stopTypeText.toLowerCase()];
  if (stopTypeText && !stopType) {
    issues.add(order, issueLabel("stopType"), "must be P, D or B", stopTypeText);
  }

  const dateTimes = parseRowDateTimes(value, order, issues);
  checkRowNumbers(value, order, issues);

  if (issues.count > issueCount) {
    return {order, orderValues: toOrderValues(value), stop: null};
  }

  const optional = (key: CsvColumnKey) => value(key) || undefined;
  return {
    order,
    orderValues: toOrderValues(value),
    stop: {
      rowNumber,
      stopType,
      name: optional("name"),
      address: value("address"),
      city: value("city"),
      state: value("state"),
      zip: value("zip"),
      phone: optional("phone"),
      room: optional("room"),
      note: optional("note"),
      earlyDateTime: dateTimes.earlyDateTime,
      scheduledDateTime: dateTimes.scheduledDateTime as string,
      lateDateTime: dateTimes.lateDateTime,
      pieces: value("pieces") ? Number(value("pieces")) : undefined,
      weight: value("weight") ? roundWeight(value("weight")) : undefined
    }
  };
};

// Lower-cased lookups of the tenant's customers, services and vehicle types; CMS matches them in any case
type KnownLookup = {customers?: Set<string>; services: Set<string>; vehicles: Set<string>};

const toKnownLookup = (known: CsvImportKnownValues): KnownLookup => ({
  customers: known.customers && new Set(known.customers.map((customer) => customer.toLowerCase())),
  services: new Set(known.services.map((service) => service.toLowerCase())),
  vehicles: new Set(known.vehicles.map((vehicle) => vehicle.toLowerCase()))
});

const buildOrder = (
  orderNumber: string,
  rows: ParsedRow[],
  issues: CsvImportIssues,
  known?: KnownLookup
): CsvImportOrder | null => {
  const issueCount = issues.count;
  const orderValues: Partial<Record<CsvColumnKey, string>> = {};
  for (const key of ORDER_LEVEL_KEYS) {
    const values = new Set(rows.map((row) => row.orderValues[key]).filter(Boolean));
    if (values.size > 1) {
      issues.add(orderNumber, issueLabel(key), "differs between the rows of an order");
    }
    orderValues[key] = [...values][0];
  }

  // CMS fails an unknown CustomerCode only when the order is created (D19), accepts any Service and replaces an
  // unknown Vehicle with the customer default, so all three are checked here
  const {customerCode, service, vehicle} = orderValues;
  if (known?.customers && customerCode && !known.customers.has(customerCode.toLowerCase())) {
    issues.add(orderNumber, issueLabel("customerCode"), "does not exist", customerCode);
  }
  if (known && service && !known.services.has(service.toLowerCase())) {
    issues.add(orderNumber, issueLabel("service"), "does not exist", service);
  }
  if (known && vehicle && !known.vehicles.has(vehicle.toLowerCase())) {
    issues.add(orderNumber, issueLabel("vehicle"), "does not exist", vehicle);
  }

  if (issues.count > issueCount || rows.some((row) => !row.stop)) {
    return null;
  }

  return {
    orderNumber,
    customerCode: orderValues.customerCode as string,
    service: orderValues.service as string,
    orderReference: orderValues.orderReference,
    vehicle: orderValues.vehicle,
    caller: orderValues.caller,
    // Stop shapes and time windows are checked by Itinerary Planning's Next step, not here
    stops: rows.map((row) => row.stop as CsvImportStop)
  };
};

/**
 * Turns raw parsed rows (first non-blank row = header) into validated orders.
 * File checks (columns, size) stop the rest, since rows can't be read without them; otherwise every row and
 * order is checked, so all the file's issues are shown at once. Row numbers are 1-based positions in the file.
 * CustomerCode, Service and Vehicle are checked against `known` when given.
 */
export const buildCsvImport = (rows: string[][], known?: CsvImportKnownValues): CsvImportResult => {
  const result: CsvImportResult = {orders: [], issues: [], ignoredColumns: [], stopCount: 0};
  const numberedRows = rows.map((row, index) => ({row, rowNumber: index + 1})).filter(({row}) => !isBlankRow(row));
  const [headerRow, ...dataRows] = numberedRows;

  if (!headerRow || dataRows.length === 0) {
    result.issues.push("The file has no rows");
    return result;
  }
  // A Windows-1252 file (Excel's default "CSV (Comma delimited)") read as UTF-8 turns accented letters into U+FFFD
  if (numberedRows.some(({row}) => row.some((cell) => cell?.includes("\uFFFD")))) {
    result.issues.push("The file is not UTF-8. Save it as 'CSV UTF-8' and choose it again");
    return result;
  }

  const columnIndexes: Partial<Record<CsvColumnKey, number>> = {};
  const headers = headerRow.row.map((header) => header.replace(/^\uFEFF/, "").trim());
  headers.forEach((header, index) => {
    const column = CSV_IMPORT_COLUMNS.find((candidate) => candidate.aliases.includes(normalizeHeader(header)));
    if (!column) {
      if (header) result.ignoredColumns.push(header);
    } else if (columnIndexes[column.key] !== undefined) {
      result.issues.push(
        `Columns '${headers[columnIndexes[column.key] as number]}' and '${header}' both map to ${column.label}`
      );
    } else {
      columnIndexes[column.key] = index;
    }
  });
  const missing = CSV_IMPORT_COLUMNS.filter((column) => column.required && columnIndexes[column.key] === undefined);
  if (missing.length > 0) {
    result.issues.push(`Missing required column(s): ${missing.map((column) => column.label).join(", ")}`);
  }

  // No stop limit here: the planning limit applies when the created jobs are selected for planning
  result.stopCount = dataRows.length;
  const orderNumberIndex = columnIndexes.orderNumber;
  if (orderNumberIndex !== undefined) {
    const orderCount = new Set(dataRows.map(({row}) => (row[orderNumberIndex] ?? "").trim()).filter(Boolean)).size;
    const maxOrders = Constants.CSV_IMPORT.MAX_ORDERS;
    if (orderCount > maxOrders) {
      result.issues.push(
        `The file has ${orderCount.toLocaleString("en-US")} orders; the maximum is ${maxOrders.toLocaleString("en-US")}`
      );
    }
  }
  if (result.issues.length > 0) {
    return result;
  }

  const lastTitledIndex = headers.reduce((last, header, index) => (header ? index : last), -1);
  const issues = new CsvImportIssues();
  const rowsByOrder = new Map<string, ParsedRow[]>();
  for (const {row, rowNumber} of dataRows) {
    const parsed = parseRow(row, rowNumber, columnIndexes, lastTitledIndex, issues);
    rowsByOrder.set(parsed.order, [...(rowsByOrder.get(parsed.order) ?? []), parsed]);
  }

  const orders: CsvImportOrder[] = [];
  const knownLookup = known && toKnownLookup(known);
  rowsByOrder.forEach((orderRows, orderNumber) => {
    const order = buildOrder(orderNumber, orderRows, issues, knownLookup);
    if (order) orders.push(order);
  });

  result.issues = issues.lines();
  result.orders = issues.count === 0 ? orders : [];
  return result;
};

/** Validates CSV text that is already in memory (e.g. the built-in example). */
export const parseCsvImportText = (text: string, known?: CsvImportKnownValues): CsvImportResult =>
  buildCsvImport(Papa.parse<string[]>(text, {delimitersToGuess: [",", "\t"]}).data, known);

// The file's distinct customer codes, read from its CustomerCode column before the file is checked
const fileCustomerCodes = (rows: string[][]) => {
  const [header, ...dataRows] = rows.filter((row) => !isBlankRow(row));
  const aliases = CSV_IMPORT_COLUMNS.find((column) => column.key === "customerCode")?.aliases ?? [];
  const index = header ? header.findIndex((cell) => aliases.includes(normalizeHeader(cell ?? ""))) : -1;
  return index < 0 ? [] : [...new Set(dataRows.map((row) => (row[index] ?? "").trim()).filter(Boolean))];
};

/**
 * Reads a .csv (comma) or .txt (comma or tab) file and validates it against the tenant's services and vehicles,
 * and against the customers `findCustomerCodes` returns for the file's codes.
 */
export const parseCsvImportFile = async (
  file: File,
  known: CsvImportKnownValues,
  findCustomerCodes?: (codes: string[]) => Promise<string[]>
): Promise<CsvImportResult> => {
  if (!/\.(csv|txt)$/i.test(file.name)) {
    return {orders: [], issues: ["Only .csv or .txt files are supported"], ignoredColumns: [], stopCount: 0};
  }
  if (file.size > Constants.CSV_IMPORT.MAX_FILE_BYTES) {
    return {orders: [], issues: ["The file is larger than 5 MB"], ignoredColumns: [], stopCount: 0};
  }

  // No header/skipEmptyLines: header detection and blank-row skipping happen in buildCsvImport
  // so reported row numbers match the file.
  const parsed = await new Promise<Papa.ParseResult<string[]>>((resolve, reject) =>
    Papa.parse<string[]>(file, {delimitersToGuess: [",", "\t"], complete: resolve, error: reject})
  );
  const quoteError = parsed.errors.find((error) => error.type === "Quotes");
  if (quoteError) {
    return {
      orders: [],
      issues: [`Row ${(quoteError.row ?? 0) + 1}: a quoted value is not closed`],
      ignoredColumns: [],
      stopCount: 0
    };
  }
  // More codes than orders allowed means the file is rejected for its order count anyway
  const codes = fileCustomerCodes(parsed.data);
  const customers =
    findCustomerCodes && codes.length > 0 && codes.length <= Constants.CSV_IMPORT.MAX_ORDERS
      ? await findCustomerCodes(codes)
      : undefined;
  return buildCsvImport(parsed.data, {...known, customers});
};

// Example offered from the dialog: two 2-stop orders and a 5-stop order (1 pickup, 4 deliveries)
export const CSV_IMPORT_EXAMPLE = [
  "OrderNumber,CustomerCode,Service,StopType,Address,City,State,Zip,ScheduledDateTime,EarlyDateTime,LateDateTime,OrderReference,Pieces,Weight,Vehicle,Caller,Name,Phone,Room,Note",
  "ORD-1,CUST1,Regular,P,6501 Horseshoe Dr,Raleigh,NC,27615,2026-10-20 08:30,2026-10-20 08:00,2026-10-20 09:00,PO-1001,2,15.5,Car,Jane Doe,Raleigh Warehouse,919-555-0101,Dock 3,",
  "ORD-1,CUST1,Regular,D,4305 Peacock Pl,Raleigh,NC,27603,2026-10-20 10:00,2026-10-20 09:30,2026-10-20 11:00,PO-1001,,,Car,Jane Doe,Oak Street Clinic,919-555-0102,,Ring the bell",
  "ORD-2,CUST1,Rush,P,200 Ashville Ave,Cary,NC,27518,2026-10-20 09:00,2026-10-20 08:30,2026-10-20 09:30,,1,4,,,Cary Distribution,919-555-0104,,",
  "ORD-2,CUST1,Rush,D,301 N Main St,Holly Springs,NC,27540,2026-10-20 11:00,2026-10-20 10:30,2026-10-20 12:00,,,,,,Holly Springs Clinic,,Suite 200,",
  "ORD-3,CUST2,Regular,P,1000 Airport Blvd,Morrisville,NC,27560,2026-10-20 08:30,2026-10-20 08:00,2026-10-20 09:00,PO-2001,8,40.25,Cargo Van,,Morrisville Depot,919-555-0112,,",
  "ORD-3,CUST2,Regular,D,100 E Franklin St,Chapel Hill,NC,27514,2026-10-20 10:00,2026-10-20 09:30,2026-10-20 11:00,PO-2001,,,Cargo Van,,Chapel Hill Office,919-555-0113,,",
  "ORD-3,CUST2,Regular,D,1525 Walnut St,Cary,NC,27511,2026-10-20 11:00,2026-10-20 10:30,2026-10-20 12:00,PO-2001,,,Cargo Van,,Walnut Pharmacy,,,",
  "ORD-3,CUST2,Regular,D,1000 Beaver Creek Commons Dr,Apex,NC,27502,2026-10-20 12:00,2026-10-20 11:30,2026-10-20 13:00,PO-2001,,,Cargo Van,,Apex Pharmacy,,,Back entrance",
  "ORD-3,CUST2,Regular,D,215 S Main St,Fuquay-Varina,NC,27526,2026-10-20 13:00,2026-10-20 12:30,2026-10-20 14:00,PO-2001,,,Cargo Van,,Fuquay Family Practice,,Suite 4,"
].join("\n");

// The same example as a tab-delimited .txt file
export const CSV_IMPORT_EXAMPLE_TXT = Papa.unparse(Papa.parse<string[]>(CSV_IMPORT_EXAMPLE).data, {
  delimiter: "\t",
  newline: "\n"
});

export const CSV_IMPORT_POLL_INTERVAL_MS = 2000;
// Polling gives up after this many failed status checks in a row...
export const CSV_IMPORT_MAX_POLL_FAILURES = 5;
// ...or when the created count hasn't changed for this long
export const CSV_IMPORT_STALL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Why creating the orders ended without a full result: "failed" when the import finished with failed orders (or
 * failed as a whole), "unconfirmed" when polling gave up or the import timed out, so the server may still be
 * creating orders. failedOrders carry the reason per order; createdOrders are the ones that were created and are
 * ready for planning, so they can still be selected.
 */
export class CsvImportCreateError extends Error {
  constructor(
    readonly reason: "failed" | "unconfirmed",
    readonly created: number,
    readonly total: number,
    readonly failedOrders: CsvImportFailedOrder[] = [],
    readonly createdOrders: CsvImportCreatedOrder[] = [],
    // The import-level error from the backend, when it failed as a whole
    readonly detail?: string
  ) {
    super(reason === "failed" ? `${total - created} of ${total} orders failed` : "Lost track of the import");
    this.name = "CsvImportCreateError";
  }
}

const abortError = () => new DOMException("Order creation polling was stopped", "AbortError");

export const isAbortError = (error: unknown) => error instanceof DOMException && error.name === "AbortError";

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, {once: true});
  });

/**
 * Waits a poll interval and gets the import status. A failed check is retried, since the server keeps creating
 * orders; after CSV_IMPORT_MAX_POLL_FAILURES in a row it throws giveUp().
 */
const pollStatus = async (
  client: CsvImportClient,
  importId: string,
  signal: AbortSignal | undefined,
  giveUp: () => Error
) => {
  for (let attempt = 1; ; attempt++) {
    await wait(CSV_IMPORT_POLL_INTERVAL_MS, signal);
    try {
      return await getCsvImportStatus(client, importId, signal);
    } catch {
      if (attempt >= CSV_IMPORT_MAX_POLL_FAILURES) throw giveUp();
    }
  }
};

// Matches the import result to the file's orders by the file's OrderNumber; orders missing from the result
// (failed ones, or a page that did not arrive) are left out
const toCreatedOrders = (
  orders: CsvImportOrder[],
  createdOrders: NonNullable<CsvImportStatus["createdOrders"]>
): CsvImportCreatedOrder[] => {
  const createdByOrderNumber = new Map(createdOrders.map((order) => [order.orderNumber, order]));
  const result: CsvImportCreatedOrder[] = [];
  for (const order of orders) {
    const createdOrder = createdByOrderNumber.get(order.orderNumber);
    if (createdOrder) {
      result.push({...order, createdOrderNumber: createdOrder.createdOrderNumber, job: createdOrder.job});
    }
  }
  return result;
};

/**
 * Creates real orders from the validated import: submits them, then polls the import status every
 * CSV_IMPORT_POLL_INTERVAL_MS, reporting the created count, until every order is created and ready for planning.
 * A failed status check is retried, since the server keeps creating orders; aborting the signal only stops polling.
 * When some orders fail, polling continues until the created ones are ready, then throws a CsvImportCreateError
 * that carries both the failed orders (with reasons) and the created ones (still selectable). An import that failed
 * as a whole throws right away.
 */
export const createCsvImportOrders = async (
  client: CsvImportClient,
  orders: CsvImportOrder[],
  onProgress: (progress: CsvImportCreateProgress) => void,
  signal?: AbortSignal
): Promise<CsvImportCreatedOrder[]> => {
  onProgress({phase: "creating", created: 0, total: orders.length, failed: 0});
  const importId = await submitCsvImportOrders(client, orders);
  let created = 0;
  let lastChangeAt = Date.now();
  for (;;) {
    const status = await pollStatus(
      client,
      importId,
      signal,
      () => new CsvImportCreateError("unconfirmed", created, orders.length)
    );
    if (signal?.aborted) throw abortError();
    const failedCount = status.failed ?? 0;
    const failedOrders = status.failedOrders ?? [];
    const partialCreated = status.createdOrders ? toCreatedOrders(orders, status.createdOrders) : [];
    // The import itself failed (timed out, lost, internal error): don't wait for the stall timeout
    if (status.status === "FAILED") {
      const reason = status.error === "TIMED_OUT" || status.error === "IMPORT_NOT_FOUND" ? "unconfirmed" : "failed";
      throw new CsvImportCreateError(reason, status.created, status.total, failedOrders, partialCreated, status.error);
    }
    // Every order was attempted and some failed: report once the created ones are ready for planning
    const finishedWithFailures = failedCount > 0 && status.created + failedCount >= status.total;
    if (finishedWithFailures && (status.createdOrders || status.created === 0)) {
      throw new CsvImportCreateError("failed", status.created, status.total, failedOrders, partialCreated);
    }
    if (status.createdOrders && !finishedWithFailures) {
      const createdOrders = toCreatedOrders(orders, status.createdOrders);
      // Every order was created but the result does not list them all: the server knows more than we got back
      if (createdOrders.length < orders.length) {
        throw new CsvImportCreateError("unconfirmed", status.created, status.total);
      }
      return createdOrders;
    }
    if (status.created !== created) {
      created = status.created;
      lastChangeAt = Date.now();
    } else if (Date.now() - lastChangeAt >= CSV_IMPORT_STALL_TIMEOUT_MS) {
      // Created orders that never became ready: report the failures if there were any, else lost track
      if (finishedWithFailures) {
        throw new CsvImportCreateError("failed", status.created, status.total, failedOrders);
      }
      throw new CsvImportCreateError("unconfirmed", created, status.total);
    }
    onProgress({
      phase: status.created + failedCount < status.total ? "creating" : "syncing",
      created: status.created,
      total: status.total,
      failed: failedCount
    });
  }
};
